/**
 * Campaign/Note Matching Utilities
 *
 * Handles recurring campaign logic for calendar display
 */

import {
  startOfDay,
  getDay,
  getDate,
  getMonth,
  isBefore,
  isAfter,
  isSameDay,
} from 'date-fns';
import { CalendarNote } from '@/types/calendar-notes';

/**
 * Check if a campaign should appear on a given day
 * Handles: non-repeating, DAILY, WEEKLY, MONTHLY, YEARLY
 */
export function campaignMatchesDay(campaign: CalendarNote, day: Date): boolean {
  const dayStart = startOfDay(day);
  const campaignStart = startOfDay(new Date(campaign.startAt));
  const campaignEnd = campaign.endAt ? startOfDay(new Date(campaign.endAt)) : null;

  // Day must be on or after campaign start
  if (isBefore(dayStart, campaignStart)) {
    return false;
  }

  // If there's an end date, day must be on or before it
  if (campaignEnd && isAfter(dayStart, campaignEnd)) {
    return false;
  }

  // Non-repeating campaign: check if day is within range
  if (!campaign.repeatType) {
    // Single-day campaign (no endAt) - must be same day
    if (!campaignEnd) {
      return isSameDay(dayStart, campaignStart);
    }
    // Multi-day campaign (has endAt) - day is already validated to be within range
    return true;
  }

  // Handle repeat types
  switch (campaign.repeatType) {
    case 'DAILY':
      // Every day within the range
      return true;

    case 'WEEKLY':
      // Same day of week as startAt
      return getDay(dayStart) === getDay(campaignStart);

    case 'MONTHLY':
      // Same day of month as startAt
      return getDate(dayStart) === getDate(campaignStart);

    case 'YEARLY':
      // Same month and day as startAt
      return (
        getMonth(dayStart) === getMonth(campaignStart) &&
        getDate(dayStart) === getDate(campaignStart)
      );

    default:
      return false;
  }
}

export type CampaignDayPosition = 'single' | 'first' | 'middle' | 'last';

/**
 * Get the position of a campaign on a specific day within its span.
 * For non-repeating multi-day campaigns: tracks first/middle/last day.
 * For repeating campaigns or single-day: always 'single'.
 */
export function getCampaignDayPosition(campaign: CalendarNote, day: Date): CampaignDayPosition {
  const dayStart = startOfDay(day);
  const campaignStart = startOfDay(new Date(campaign.startAt));
  const campaignEnd = campaign.endAt ? startOfDay(new Date(campaign.endAt)) : null;

  // Repeating campaigns always render as single (they appear on individual days)
  if (campaign.repeatType) {
    return 'single';
  }

  // Single-day campaign (no endAt or same start/end)
  if (!campaignEnd || isSameDay(campaignStart, campaignEnd)) {
    return 'single';
  }

  // Multi-day non-repeating campaign
  if (isSameDay(dayStart, campaignStart)) return 'first';
  if (isSameDay(dayStart, campaignEnd)) return 'last';
  return 'middle';
}

/**
 * Slot assignment for consistent vertical positioning of multi-day campaigns.
 * Each campaign gets a stable slot index across all days it spans,
 * so the bar stays on the same visual row (like Google Calendar).
 *
 * Returns a Map of campaignId → slot index (0-based).
 */
export function assignCampaignSlots(
  campaigns: CalendarNote[] | undefined,
  calendarDays: Date[]
): Map<number, number> {
  if (!campaigns || campaigns.length === 0) return new Map();

  // Build a map of campaignId → set of day indices where it appears
  const campaignDays = new Map<number, Set<number>>();
  const campaignById = new Map<number, CalendarNote>();

  for (let dayIdx = 0; dayIdx < calendarDays.length; dayIdx++) {
    const day = calendarDays[dayIdx];
    for (const c of campaigns) {
      if (campaignMatchesDay(c, day)) {
        if (!campaignDays.has(c.id)) {
          campaignDays.set(c.id, new Set());
          campaignById.set(c.id, c);
        }
        campaignDays.get(c.id)!.add(dayIdx);
      }
    }
  }

  // Sort: continuous multi-day spans first (top slots), then by earliest day, then longer spans first
  // A "continuous" multi-day campaign is non-repeating with endAt different from startAt
  const isMultiDaySpan = (id: number): boolean => {
    const c = campaignById.get(id);
    if (!c || c.repeatType) return false; // repeating = individual appearances, not a span
    if (!c.endAt) return false;
    return !isSameDay(startOfDay(new Date(c.startAt)), startOfDay(new Date(c.endAt)));
  };

  const sortedIds = [...campaignDays.keys()].sort((a, b) => {
    const aMulti = isMultiDaySpan(a) ? 0 : 1;
    const bMulti = isMultiDaySpan(b) ? 0 : 1;
    if (aMulti !== bMulti) return aMulti - bMulti; // continuous spans first
    const aDays = campaignDays.get(a)!;
    const bDays = campaignDays.get(b)!;
    const aMin = Math.min(...aDays);
    const bMin = Math.min(...bDays);
    if (aMin !== bMin) return aMin - bMin;
    return bDays.size - aDays.size; // longer spans first
  });

  // Greedy slot allocation — assign each campaign the lowest slot
  // that doesn't conflict with already-assigned campaigns on the same days
  const slotMap = new Map<number, number>();
  // Track which slots are used on each day: dayIdx → Set<slot>
  const daySlotUsage: Map<number, Set<number>> = new Map();

  for (const cId of sortedIds) {
    const days = campaignDays.get(cId)!;
    // Find the lowest slot not used on any of this campaign's days
    let slot = 0;
    while (true) {
      let conflict = false;
      for (const dayIdx of days) {
        if (daySlotUsage.get(dayIdx)?.has(slot)) {
          conflict = true;
          break;
        }
      }
      if (!conflict) break;
      slot++;
    }
    slotMap.set(cId, slot);
    // Mark this slot as used on all this campaign's days
    for (const dayIdx of days) {
      if (!daySlotUsage.has(dayIdx)) daySlotUsage.set(dayIdx, new Set());
      daySlotUsage.get(dayIdx)!.add(slot);
    }
  }

  return slotMap;
}

/**
 * Get the maximum slot index used on a specific day.
 * Returns -1 if no campaigns on that day.
 */
export function getMaxSlotForDay(
  campaigns: CalendarNote[],
  slotMap: Map<number, number>
): number {
  let max = -1;
  for (const c of campaigns) {
    const slot = slotMap.get(c.id);
    if (slot !== undefined && slot > max) max = slot;
  }
  return max;
}

/**
 * Filter campaigns for a specific day
 */
export function filterCampaignsForDay(
  campaigns: CalendarNote[] | undefined,
  day: Date
): CalendarNote[] {
  if (!campaigns) return [];
  return campaigns.filter((campaign) => campaignMatchesDay(campaign, day));
}
